import { ENDPOINTS, getFullUrl } from "./config";
import { OverviewCarousel } from "./types";

export class OverviewService {
  static getToken(): string | null {
    return localStorage.getItem("token");
  }

  static async getOverviewCarousel(): Promise<OverviewCarousel | null> {
    try {
      const token = this.getToken();
      if (!token) {
        throw new Error("No token found");
      }

      // Get user details from localStorage
      const savedUser = localStorage.getItem("auth_user");
      const user = savedUser ? JSON.parse(savedUser) : null;

      if (!user || !user.tokenData) {
        throw new Error("Invalid user data");
      }

      // Extract role and normalize to lowercase
      const role = user.tokenData.role.toLowerCase();
      const clinicId = role === "clinic" ? user.tokenData.id : undefined;
      const campId = role === "medicalcamp" ? user.tokenData.id : undefined;

      // Construct the correct URL with query params
      let url = getFullUrl(ENDPOINTS.OVERVIEW.GETCAROUSELDATA);
      if (clinicId) {
        url += `?clinicId=${clinicId}`;
      }

      if (campId) {
        url += `?campId=${campId}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status} - ${response.statusText}`);
      }

      const data: OverviewCarousel = await response.json();
      return data;
    } catch (error) {
      console.error("Failed to fetch overview carousel:", error);
      return null;
    }
  }

  static async getAppointmentAnalysis(): Promise<any | null> {
    try {
      const token = this.getToken();
      if (!token) {
        throw new Error("No token found");
      }

      const savedUser = localStorage.getItem("auth_user");
      const user = savedUser ? JSON.parse(savedUser) : null;

      if (!user || !user.tokenData) {
        throw new Error("Invalid user data");
      }

      const role = user.tokenData.role.toLowerCase();
      const clinicId = role === "clinic" ? user.tokenData.id : undefined;
      const campId = role === "medicalcamp" ? user.tokenData.id : undefined;

      let url = getFullUrl(ENDPOINTS.OVERVIEW.APPOINTMENTANALYSIS);
      if (clinicId) {
        url += `?clinicId=${clinicId}`;
      }

      if (campId) {
        url += `?campId=${campId}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status} - ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to fetch appointment analysis:", error);
      return null;
    }
  }

  static async getAdminClinicPerformance(order: "asc" | "desc") {
    try {
      const token = this.getToken();
      if (!token) throw new Error("No token found");

      const url =
        getFullUrl(ENDPOINTS.OVERVIEW.ADMINCLINICPERFORMANCE) +
        `?order=${order}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok)
        throw new Error(`Error: ${response.status} - ${response.statusText}`);

      const result = await response.json();
      return result.data || [];
    } catch (error) {
      console.error("Failed to fetch clinic performance:", error);
      return [];
    }
  }

  static async fetchRevenueStats(): Promise<
    { month: string; revenue: number }[]
  > {
    const token = this.getToken();
    if (!token) throw new Error("No authentication token found");

    const response = await fetch(
      getFullUrl(ENDPOINTS.OVERVIEW.ADMINREVENUEGRAPH),
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch revenue stats");
    }

    const result = await response.json();
    return result.data.map(
      ({ month, sum }: { month: string; sum: number }) => ({
        month,
        revenue: sum,
      })
    );
  }
}
